import { readFile } from 'node:fs/promises';
import { outputFile, pathExists } from '@wix/cli-fs';
import camelCase from 'lodash/camelCase.js';
import type { JSCodeshift } from 'jscodeshift';
import jscodeshift from 'jscodeshift';

export async function modifyDataCollectionsExtension(
  filePath: string,
  collectionName: string
) {
  if (!(await pathExists(filePath))) {
    return false;
  }

  const source = await readFile(filePath, 'utf-8');
  const j = jscodeshift.withParser('tsx');
  const root = j(source);

  const collectionImportName = ensureCollectionImport(root, j, collectionName);

  ensureCollectionReference(root, j, collectionImportName);

  const modifiedCode = root.toSource({ quote: 'single' });

  if (modifiedCode === source) {
    return false;
  }

  await outputFile(filePath, modifiedCode);
  return true;
}

function ensureCollectionImport(
  root: ReturnType<JSCodeshift>,
  j: JSCodeshift,
  collectionName: string
) {
  const importPath = `./${collectionName}`;
  const existingImport = root.find(j.ImportDeclaration, {
    source: { value: importPath },
  });

  if (existingImport.length > 0) {
    const existingImportPath = existingImport.paths()[0];
    const defaultSpecifier = existingImportPath?.node.specifiers?.find(
      (specifier) => specifier.type === 'ImportDefaultSpecifier'
    );

    if (defaultSpecifier?.local?.name) {
      return defaultSpecifier.local.name;
    }
  }

  const localName = getUniqueLocalName(
    root,
    j,
    getCollectionImportName(collectionName)
  );
  const newImport = j.importDeclaration(
    [j.importDefaultSpecifier(j.identifier(localName))],
    j.literal(importPath)
  );
  const lastImport = root.find(j.ImportDeclaration).at(-1);

  if (lastImport.length > 0) {
    lastImport.insertAfter(newImport);
  } else {
    root.get().node.program.body.unshift(newImport);
  }

  return localName;
}

function ensureCollectionReference(
  root: ReturnType<JSCodeshift>,
  j: JSCodeshift,
  collectionImportName: string
) {
  const dataCollectionsCall = root.find(j.CallExpression, {
    callee: {
      type: 'MemberExpression',
      property: { type: 'Identifier', name: 'dataCollections' },
    },
  });

  if (dataCollectionsCall.length === 0) {
    return;
  }

  const dataCollectionsPath = dataCollectionsCall.paths()[0];
  const options = dataCollectionsPath?.node.arguments[0];

  if (options?.type !== 'ObjectExpression') {
    return;
  }

  const collectionsProperty = options.properties.find(
    (property) =>
      property.type === 'ObjectProperty' &&
      ((property.key.type === 'Identifier' &&
        property.key.name === 'collections') ||
        (property.key.type === 'StringLiteral' &&
          property.key.value === 'collections'))
  );

  if (
    collectionsProperty?.type === 'ObjectProperty' &&
    collectionsProperty.value.type === 'ArrayExpression'
  ) {
    const hasCollectionReference = collectionsProperty.value.elements.some(
      (element) =>
        element?.type === 'Identifier' && element.name === collectionImportName
    );

    if (!hasCollectionReference) {
      collectionsProperty.value.elements.push(
        j.identifier(collectionImportName)
      );
    }

    return;
  }

  options.properties.push(
    j.objectProperty(
      j.identifier('collections'),
      j.arrayExpression([j.identifier(collectionImportName)])
    )
  );
}

function getCollectionImportName(collectionName: string) {
  const importName = camelCase(`${collectionName}-collection`);

  return /^[A-Za-z_$]/.test(importName)
    ? importName
    : `collection${importName[0]?.toUpperCase() ?? ''}${importName.slice(1)}`;
}

function getUniqueLocalName(
  root: ReturnType<JSCodeshift>,
  j: JSCodeshift,
  localName: string
) {
  let uniqueLocalName = localName;
  let counter = 1;
  const allSpecifiers = root
    .find(j.ImportSpecifier)
    .paths()
    .map((path) => path.node.local?.name)
    .concat(
      root
        .find(j.ImportDefaultSpecifier)
        .paths()
        .map((path) => path.node.local?.name)
    );

  while (allSpecifiers.includes(uniqueLocalName)) {
    uniqueLocalName = `${localName}${counter++}`;
  }

  return uniqueLocalName;
}
